/**
 * GPU data layouts — the cheatsheet §3.10 r3 production contract (sizes verified by tests/engine/contract.test.ts):
 * `Frame` 256 B, `SceneLight` 608 B (24 packed contacts + expedition parameters), `CreatureLook` 528 B × 12, `bones` / `bonesLag` 576 × mat4,
 * `bindPivot` 576 × vec4, `smear` 12 × vec4, props 32 × mat4 (+ 32 × vec4u info), the 24 B creature vertex and the
 * 20 B hull vertex. Plus engine-internal uniforms: `MatRamps` (fixed material ramps) and `Post` (composite).
 */
import tgpu from 'typegpu';
import * as d from 'typegpu/data';
import { MAX_BONES, MAX_CONTACTS, MAX_PROPS, MAX_SLOTS } from '../../shared';

export { MAX_BONES, MAX_CONTACTS, MAX_PROPS, MAX_SLOTS };

export const CreatureLook = d.struct({
  rampBase: d.arrayOf(d.vec4f, 3),
  rampStripe: d.arrayOf(d.vec4f, 3),
  rampBelly: d.arrayOf(d.vec4f, 3),
  rampFlesh: d.arrayOf(d.vec4f, 3),
  rampDye: d.arrayOf(d.vec4f, 12),
  strokeInk: d.vec4f,
  iris: d.vec4f,
  pattern: d.vec4u,
  patParams: d.vec4f,
  headFrame: d.vec4f,
  line: d.vec4f,
  fx: d.vec4f,
  face: d.vec4f,
  fxColor: d.vec4f,
});

/** Float offsets inside `SceneLight` (host mirror). */
export const SL = {
  keyDir: 0, keyTint: 4, shadowTint: 8, rim: 12, hatch: 16, torch: 20, contact: 36, timing: 132, viewportPx: 136,
  panelRectPx: 140, qualityFlags: 144, expedition:148, floats: 152,
} as const;
export const SceneLight = d.struct({
  keyDir: d.vec4f, //       xyz key direction (to the light), w = band T1
  keyTint: d.vec4f, //      rgb, w = band T2
  shadowTint: d.vec4f, //   rgb, w = hatch strength
  rim: d.vec4f, //          rgb, w = strength
  hatch: d.vec4f, //        x rim threshold, y hatch spacing (device px), z hatch layers, w fur-stroke density
  torch: d.arrayOf(d.vec4f, 4),
  contact: d.arrayOf(d.vec4f, MAX_CONTACTS), // foot x, z, height above ground (H), radius (H)
  timing: d.vec4f, //       x shader time s, y time scale, z backdrop id, w backdrop fade
  viewportPx: d.vec4f, //   xy scene target px, z device px per CSS px in the scene target, w quality tier 0..3
  panelRectPx: d.vec4f, //  picture interior in scene target px (x, y, w, h)
  qualityFlags: d.vec4u, // x tier, y contact count, z feature bits, w debug view
  expedition:d.vec4f, // seed, biome 0..11, weather 0..3, accumulated travel
});

/** Float offsets inside `Frame`. */
export const FR = { viewProj: 0, view: 16, proj: 32, eye: 48, timing: 52, viewport: 56, subPanel: 60, floats: 64 } as const;
export const Frame = d.struct({
  viewProj: d.mat4x4f, view: d.mat4x4f, proj: d.mat4x4f,
  eye: d.vec4f, //     xyz camera position
  timing: d.vec4f, //  x time s, y aspect, z near, w far
  viewport: d.vec4f, // xy scene target px, z device px per CSS px, w render scale
  subPanel: d.vec4f, // reserved for the triptych (x, y, w, h of the live band in scene px)
});

/** Fixed material ramps (light, mid, shadow) for non-creature materials, derived on the CPU per scene tint. */
export const MAT_RAMP_COUNT = 12;
export const MATR = { leather: 0, wood: 1, iron: 2, stone: 3, horn: 4, shell: 5, parchment: 6, floorA: 7, floorB: 8, plumage: 9, sclera: 10, gem: 11 } as const;
export const MatRamps = d.arrayOf(d.vec4f, MAT_RAMP_COUNT * 3);

/** Composite uniforms (float offsets for the host mirror). */
export const PO = {
  panel: 0, picture: 4, frame: 8, shake: 12, fx: 16, impact: 20, grade: 24, view: 28, canvas: 32, sub: 36, subFlags: 48, floats: 52,
} as const;
export const Post = d.struct({
  panel: d.vec4f, //   outer panel rect, canvas device px (x, y, w, h)
  picture: d.vec4f, // picture interior rect, canvas device px
  frame: d.vec4f, //   x frame kind, y archK, z band px (device), w band scale s
  shake: d.vec4f, //   x, y offset (device px), z rotation (rad), w frame-break present
  fx: d.vec4f, //      x time s, y grain strength, z burn, w vignette strength
  impact: d.vec4f, //  x frame (0 none, 1 negative, 2 white), y crit/ko (1) vs (0), z vignette pulse, w pulse vermilion
  grade: d.vec4f, //   x exposure, y wb red gain, z saturation, w split-tone (lapis)
  view: d.vec4f, //    x debug view, y device px per CSS px, z scene scale (scene px per canvas px), w ink-bleed taps
  canvas: d.vec4f, //  xy canvas px, zw scene px
  sub: d.arrayOf(d.vec4f, 3), // triptych sub-panel rects, canvas device px (art §7.3)
  subFlags: d.vec4f, // x count, y frozen bits (1, 2, 4), z divider progress 0..1, w dissolve 0..1 (1 = bands fully shown)
});

export const CreatureVertex = d.unstruct({ pos: d.float32x3, pat: d.unorm16x2, skin: d.uint8x4, info: d.uint8x4 });
export const HullVertex = d.unstruct({ pos: d.float32x3, nrm: d.snorm8x4, skin: d.uint8x4 });
export const creatureVertexLayout = tgpu.vertexLayout((n: number) => d.disarrayOf(CreatureVertex, n));
export const hullVertexLayout = tgpu.vertexLayout((n: number) => d.disarrayOf(HullVertex, n));

export const Particle = d.struct({ posSize: d.vec4f, color: d.vec4f, params: d.vec4f }); // 48 B
export const particleLayout = tgpu.vertexLayout((n: number) => d.arrayOf(Particle, n), 'instance');
export const trailLayout = tgpu.vertexLayout((n: number) => d.arrayOf(Particle, n));
export const PARTICLE_FLOATS = 12;

export const frameLayout = tgpu.bindGroupLayout({
  frame: { uniform: Frame },
  light: { uniform: SceneLight },
  mats: { uniform: MatRamps },
});
export const skinLayout = tgpu.bindGroupLayout({
  bones: { storage: d.arrayOf(d.mat4x4f, MAX_BONES * MAX_SLOTS), access: 'readonly' },
  bonesLag: { storage: d.arrayOf(d.mat4x4f, MAX_BONES * MAX_SLOTS), access: 'readonly' },
  bindPivot: { storage: d.arrayOf(d.vec4f, MAX_BONES * MAX_SLOTS), access: 'readonly' },
  smear: { uniform: d.arrayOf(d.vec4f, MAX_SLOTS) },
  looks: { uniform: d.arrayOf(CreatureLook, MAX_SLOTS) },
});
/** Props: one matrix + one info vec4u (x = look slot or 0xFFFF neutral, y = flags bit0 mirrored, z = material override). */
export const propLayout = tgpu.bindGroupLayout({
  props: { storage: d.arrayOf(d.mat4x4f, MAX_PROPS), access: 'readonly' },
  propInfo: { storage: d.arrayOf(d.vec4u, MAX_PROPS), access: 'readonly' },
});
export const postLayout = tgpu.bindGroupLayout({
  scene: { texture: d.texture2d(d.f32) },
  held: { texture: d.texture2d(d.f32) },
  samp: { sampler: 'filtering' },
  post: { uniform: Post },
});
export const illustrationLayout = tgpu.bindGroupLayout({
  page: { texture: d.texture2d(d.f32) },
  samp: { sampler: 'filtering' },
});

/** Bytes of each buffer (also asserted by the contract test). */
export const BYTES = {
  frame: d.sizeOf(Frame), light: d.sizeOf(SceneLight), look: d.sizeOf(CreatureLook), looks: d.sizeOf(d.arrayOf(CreatureLook, MAX_SLOTS)),
  bones: d.sizeOf(d.arrayOf(d.mat4x4f, MAX_BONES * MAX_SLOTS)), bindPivot: d.sizeOf(d.arrayOf(d.vec4f, MAX_BONES * MAX_SLOTS)),
  smear: d.sizeOf(d.arrayOf(d.vec4f, MAX_SLOTS)), props: d.sizeOf(d.arrayOf(d.mat4x4f, MAX_PROPS)), propInfo: d.sizeOf(d.arrayOf(d.vec4u, MAX_PROPS)),
  mats: d.sizeOf(MatRamps), post: d.sizeOf(Post), particle: d.sizeOf(Particle),
} as const;

/** Backdrop ids in `SceneLight.timing.z`. */
export const BACKDROP_ID = { vault: 0, diaper: 1, quatrefoil: 2, gold: 3, fresco: 4, vellum: 5, cloister: 6 } as const;
/** Frame kind ids in `Post.frame.x`. */
export const FRAME_ID = { none: 0, rect: 1, pointedArch: 2, roundArch: 3, trefoil: 4, quatrefoil: 5 } as const;
/** `SceneLight.qualityFlags.z` feature bits. */
export const QF = { furStrokes: 1, hatch2: 2, hatchGaps: 4, sparkle: 8, punchwork: 16, mottling: 32, granulation: 64, wobble: 128, hueWobble: 256, smear: 512 } as const;
/** Debug views (`rdebug=`) in `qualityFlags.w` / `Post.view.x`. */
export const DEBUG_VIEW = { none: 0, bands: 1, lines: 2, normals: 3, ids: 4, mask: 5, alpha: 6, pattern: 7, hatch: 8, nopost: 9 } as const;
export type DebugViewName = keyof typeof DEBUG_VIEW;
